import { PrimaryButton } from "@digipost/dds-buttons-react";
import { Details } from "@digipost/dds-details-react";
import { Heading } from "@digipost/dds-heading-react";
import Infobox from "@digipost/dds-infobox-react";
import { Input, TextArea } from "@digipost/dds-input-react";
import { useLanguage } from "@digipost/translate";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons/faExclamationCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ChangeEvent, FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import InfoWarningText from "../InfoWarningText";
import { Dispatch } from "../infrastructure/redux/actions";
import {
  organitionPendingAction,
  RegistrationStatus,
  setUserInputAction,
} from "../infrastructure/redux/org/org-actions";
import { ReduxState } from "../infrastructure/redux/state";
import Translated from "../infrastructure/Translated";
import { useTranslate } from "../infrastructure/useTranslate";
import { Postmottak } from "../registrer/Postmottak";
import RegisterFlowStepper from "../registrer/RegisterFlowStepper";
import styles from "./ManuellSkjema.module.css";
import { isValidFormValue } from "./validateForm";

export const ManuellSkjema: FC = () => {
  const [invalidInputs, setInvalidInputs] = useState<string[]>([]);

  const preselectedOrg = useSelector(
    (state: ReduxState) => state.app.params.preselectedOrg,
  );
  const nonce = useSelector((state: ReduxState) => state.app.params.nonce);

  const [name, setName] = useState<string>("");
  const [kommentar, setKommentar] = useState<string>("");
  const [activateMailbox, setActivateMailbox] = useState<boolean>(false);

  const dispatch = useDispatch<Dispatch>();
  const translate = useTranslate();
  const { language } = useLanguage();

  const validationRules = useSelector(
    (state: ReduxState) => state.app.validationRules,
  );
  const registrationStatus = useSelector(
    (state: ReduxState) => state.org.registrationStatus,
  );
  const selectedIntegrationPartner = useSelector(
    (state: ReduxState) => state.org.userInput.selectedIntegrationPartner,
  );
  const firstName = useSelector(
    (state: ReduxState) => state.user.info?.firstName,
  );
  const lastName = useSelector(
    (state: ReduxState) => state.user.info?.lastName,
  );
  const { email, phone, selectedOrganisationNumber } = useSelector(
    (state: ReduxState) => state.org.userInput,
  );

  const userName = `${firstName} ${lastName}`;

  const validate = (): boolean => {
    const newInvalidInputs = [];

    if (
      !isValidFormValue(
        selectedOrganisationNumber,
        true,
        validationRules,
        "organisationNumber",
      )
    ) {
      newInvalidInputs.push("organisationNumber");
    }

    if (!name?.length) {
      newInvalidInputs.push("name");
    }

    if (!isValidFormValue(email, true, validationRules, "email")) {
      newInvalidInputs.push("email");
    }

    if (!isValidFormValue(phone, true, validationRules, "phoneNumber")) {
      newInvalidInputs.push("telephoneNumber");
    }
    setInvalidInputs([...newInvalidInputs]);
    return newInvalidInputs.length === 0;
  };

  const submitManuellSkjema = (): void => {
    const valid = validate();
    if (valid) {
      dispatch(
        organitionPendingAction({
          organisationNumber: selectedOrganisationNumber,
          email: email,
          telephoneNumber: phone,
          language: language,
          activateMailbox: activateMailbox,
          name: name,
          integrationPartnerId: selectedIntegrationPartner
            ? selectedIntegrationPartner.senderId
            : undefined,
          externalPartnerNonce: nonce ?? undefined,
          registrationContext: "digipost",
          kommentar: kommentar,
        }),
      );
    }
  };
  const isFieldValid = (field: string): boolean => {
    return !invalidInputs.includes(field);
  };

  useEffect(() => {
    document.title = `Digipost - ${translate("register.org.intro.header")}`;
  }, [translate]);

  useEffect(() => {
    dispatch(setUserInputAction({ selectedOrganisationNumber: "" }));
  }, [dispatch]);

  useEffect(() => {
    if (preselectedOrg) {
      dispatch(
        setUserInputAction({
          selectedOrganisationNumber: preselectedOrg,
        }),
      );
    }
  }, [dispatch, preselectedOrg]);

  return (
    <main>
      <Heading level={1} size="large">
        <Translated textKey="register.org.registration_form.title" />
      </Heading>
      <RegisterFlowStepper currentStep="MANUAL_ACCOUNT_DETAILS" />

      <div className={styles.formContent}>
        <p>
          <Translated textKey="register.org.intro.digipost" />
        </p>
        <Infobox
          title={translate(
            "register.org.insufficient_access.manual.time_warning.title",
          )}
          variant="info"
          level={2}
        >
          <Translated textKey="register.org.insufficient_access.manual.time_warning" />
        </Infobox>
        <div className={styles.formSection}>
          <Heading level={2} size="medium">
            <Translated textKey="register.org.manual.org_info.title" />
          </Heading>
          <Input
            name="organisationNumber"
            valid={isFieldValid("organisationNumber")}
            errorIcon={<FontAwesomeIcon icon={faExclamationCircle} />}
            errorText={
              !isFieldValid("organisationNumber")
                ? "Ugyldig organisasjonsnummer"
                : undefined
            }
            onChange={(event) => {
              dispatch(
                setUserInputAction({
                  selectedOrganisationNumber: event.target.value,
                }),
              );
            }}
            labelText={`${translate("generic.organisation_number")}*`}
            data-testid="manual-schema-organisation-number-input"
            disabled={!!preselectedOrg}
            value={selectedOrganisationNumber}
            descriptionText={
              preselectedOrg &&
              translate(
                "register.org.insufficient_access.mandatory_org.info_text",
                { systemleverandoer: selectedIntegrationPartner?.name },
              )
            }
          />

          <Input
            name="name"
            valid={isFieldValid("name")}
            errorIcon={<FontAwesomeIcon icon={faExclamationCircle} />}
            errorText={
              !isFieldValid("name") ? "Påkrevet navn på virksomhet" : undefined
            }
            onChange={(event) => setName(event.target.value)}
            labelText={`Navn på virksomhet*`}
            data-testid="manual-schema-organisation-name-input"
          />
        </div>
        <div className={styles.formSection}>
          <Heading level={2} size="medium">
            <Translated textKey="register.org.manual.contact_info.title" />
          </Heading>
          <Input
            name="username"
            labelText={`Ditt navn*`}
            disabled={true}
            value={userName}
            data-testid="manual-schema-user-name-input"
          />
          <Input
            name="email"
            valid={isFieldValid("email")}
            errorIcon={<FontAwesomeIcon icon={faExclamationCircle} />}
            errorText={
              !isFieldValid("email")
                ? translate("register.org.registration_form.invalid_email")
                : undefined
            }
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              dispatch(setUserInputAction({ email: event.target.value }));
            }}
            labelText={`${translate("register.your_email")}*`}
            data-testid="manual-schema-email-input"
          />
          <Input
            name="telephoneNumber"
            valid={isFieldValid("telephoneNumber")}
            errorIcon={<FontAwesomeIcon icon={faExclamationCircle} />}
            errorText={
              !isFieldValid("telephoneNumber")
                ? translate("register.org.registration_form.invalid_phone")
                : undefined
            }
            onChange={(event) => {
              dispatch(setUserInputAction({ phone: event.target.value }));
            }}
            inputType="phone"
            labelText={`${translate("register.your_phone_number")}*`}
            data-testid="manual-schema-phone-input"
          />
        </div>
        <div className={styles.formSection}>
          <Postmottak
            value={activateMailbox}
            onChange={() => setActivateMailbox(!activateMailbox)}
          />
        </div>
        <div className={styles.formSection}>
          <Heading level={2} size="medium">
            <Translated textKey="register.org.manual.other.title" />
          </Heading>
          <TextArea
            id="kommentar"
            onChange={(event) => setKommentar(event.target.value)}
            name="kommentar"
            rows={3}
            data-testid="manual-schema-comment-textarea"
            labelText={"Kommentar"}
          />
          <Details summary={translate("register.org.manual.comment.summary")}>
            <Translated
              textKey={"register.org.manual.comment.details"}
              allowHtml={true}
            />
          </Details>
        </div>
        {registrationStatus === RegistrationStatus.FAILED && (
          <InfoWarningText red={true} textkey="generic.error" />
        )}
        {registrationStatus ===
          RegistrationStatus.ORGANISATION_ALREADY_INTEGRATED && (
          <InfoWarningText
            red={true}
            textkey="register.org.manual.failed_already_integrated"
          />
        )}
        <PrimaryButton
          id="til_godkjenning"
          onClick={() => submitManuellSkjema()}
          data-testid="manual-schema-send-to-approval-button"
          className={styles.sendButton}
        >
          <Translated textKey="register.org.manual.form.buttonSubmit" />
        </PrimaryButton>
      </div>
    </main>
  );
};
